// 偵測到的設備（DEV-16，SPEC #vms-web-detected）：以節點子樹為範圍，列現場發現、還沒加入的設備。
//   - 頁面開著才收：進來先 PUT scan 開租約、每 60 秒續約，離開 DELETE；清單每 5 秒重抓（Station 要位址的設備每次都重問）
//   - 〔掃描 ONVIF〕：對子樹每台閘道同時發 ONVIF 探索（同步，最多約 15 秒），結果保留 10 分鐘
//   - 動作照 adopt：one_click → 既有配對流程（帶這台的 ID）；onvif → 那台閘道的設備頁（ONVIF 納管）；approve_address → 那台 Station 的設備頁（NUI-01 核准）
//   - 鄰近發現（協助模式）平台端還沒有；收編 API（POST …/adopt）等加入程序（kinio/plan#40 I3）
import { useEffect, useMemo, useState } from 'react';
import { idempotencyKey } from '@kinio/web-sdk';
import { Dialog } from '../../components/Dialog';
import type { PairTarget } from '../devices/PairDialog';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useScope } from '../../lib/scope';
import { detectedApi, type DetectedDevice, type MyOrg } from '../../lib/planeApi';
import { treeApi } from '../../lib/orgTreeApi';
import { errText } from '../settings/OrgSection';
import { PairDialog } from '../devices/PairDialog';
import { homeTargets } from '../orgtree/homeTargets';
import { timeStamp } from '../../lib/timefmt';

/** 機種或廠商；尾碼或 MAC（畫面不出現完整 device_id，UX-09）。 */
export function detectedLabel(d: Pick<DetectedDevice, 'model' | 'oui_vendor' | 'last6' | 'mac'>): { what: string; ident: string } {
  return { what: d.model || d.oui_vendor || '—', ident: d.last6 ? '…' + d.last6 : d.mac || '—' };
}

export function DetectedPanel({ org }: { org: MyOrg }) {
  const { t } = useTranslation();
  const { scope } = useScope();
  const scopeNode = scope.startsWith(`node:${org.org_id}:`) ? scope.split(':')[2] : '';
  // 節點選單：根節點 + 第一層節點（範圍選到節點時預設用它）
  const top = useQuery({ queryKey: ['ot', org.org_id, 'top-nodes'], queryFn: () => treeApi.children(org), retry: false });
  const options = useMemo(() => {
    const out: { id: string; name: string }[] = [];
    // 根節點寫「<組織>（整個組織）」，不露根節點的內部名（第二輪 B2-23）
    if (top.data?.root) out.push({ id: top.data.root.node_id, name: t('ot.whole_org_name', { org: org.display_name }) });
    for (const it of top.data?.items ?? []) if (it.kind === 'node' && it.node_id) out.push({ id: it.node_id, name: it.name });
    return out;
  }, [top.data, t, org.display_name]);
  // 手動選的節點只在同一個 org、同一個範圍內有效（換 org / 範圍就回預設）
  const [pickedFor, setPickedFor] = useState({ key: '', node: '' });
  const pickKey = org.org_id + '|' + scope;
  const picked = pickedFor.key === pickKey && options.some((o) => o.id === pickedFor.node) ? pickedFor.node : '';
  const setPicked = (n: string) => setPickedFor({ key: pickKey, node: n });
  const node = picked || scopeNode || options[0]?.id || '';

  // 租約：進來開、每 60 秒續、離開停（開 / 續失敗要讓人看得到：清單可能不是即時的）
  const [leaseErr, setLeaseErr] = useState<unknown>(null);
  useEffect(() => {
    if (!node) return;
    const renew = () =>
      detectedApi.scan(org, node).then(
        () => setLeaseErr(null),
        (e: unknown) => setLeaseErr(e),
      );
    void renew();
    const timer = setInterval(() => void renew(), 60_000);
    return () => {
      clearInterval(timer);
      void detectedApi.stop(org, node).catch(() => undefined);
    };
  }, [org, node]);

  const q = useQuery({ queryKey: ['detected', org.org_id, node], queryFn: () => detectedApi.list(org, node), enabled: !!node, retry: false, refetchInterval: 5_000 });
  const scan = useMutation({ mutationFn: (n: string) => detectedApi.scan(org, n, true).then((r) => ({ ...r, node: n })), onSuccess: () => void q.refetch() });
  const scanResult = scan.data?.node === node ? scan.data : undefined; // 換節點後不顯示別的節點的掃描結果
  const [pairAid, setPairAid] = useState<string | null>(null);
  const [adoptRow, setAdoptRow] = useState<DetectedDevice | null>(null);
  const targets = useQuery({ queryKey: ['ot', org.org_id, 'pair-targets'], queryFn: () => homeTargets(org), enabled: pairAid != null || adoptRow != null, retry: false });
  const rows = q.data?.detected_devices ?? [];

  return (
    <section className="card" data-testid="detected">
      <div className="toolbar">
        <h2 className="section-title">{t('inv.tab_detected')}</h2>
        <div className="spacer" />
        {options.length > 0 && (
          <select className="input" style={{ width: 'auto' }} aria-label={t('inv.det_node')} value={node} onChange={(e) => setPicked(e.target.value)}>
            {scopeNode && !options.some((o) => o.id === scopeNode) && <option value={scopeNode}>{t('inv.det_scope_node')}</option>}
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        )}
        <button className="btn btn-sm" disabled={!node || scan.isPending} onClick={() => scan.mutate(node)} data-testid="det-scan">
          {scan.isPending ? t('inv.det_scanning') : t('inv.det_scan')}
        </button>
      </div>
      <p className="muted small">{t('inv.detected_hint')}</p>
      <p className="muted small">{t('inv.det_nearby_pending')}</p>
      {scanResult?.onvif && (
        <p className="small" data-testid="det-scan-result">
          {t('inv.det_scan_result', { gateways: scanResult.onvif.gateways, found: scanResult.onvif.found, failed: scanResult.onvif.failed })}
        </p>
      )}
      {top.isLoading && <p className="muted small">{t('common.loading')}</p>}
      {top.isError && !scopeNode && <p className="error">{errText(t, top.error)}</p>}
      {leaseErr != null && (
        <p className="error" data-testid="det-lease-error">
          {t('inv.det_lease_error')} {errText(t, leaseErr)}
        </p>
      )}
      {scan.isError && <p className="error">{errText(t, scan.error)}</p>}
      {pairAid != null && targets.isLoading && <p className="muted small">{t('common.loading')}</p>}
      {pairAid != null && targets.isError && <p className="error">{errText(t, targets.error)}</p>}
      {q.isError && <p className="error">{errText(t, q.error)}</p>}
      <PairDialog open={pairAid != null && !!targets.data} close={() => setPairAid(null)} targets={targets.data} initialAid={pairAid ?? undefined} />
      {adoptRow != null && targets.isLoading && <p className="muted small">{t('common.loading')}</p>}
      {adoptRow != null && targets.isError && <p className="error">{errText(t, targets.error)}</p>}
      <AdoptDialog
        org={org}
        node={node}
        row={targets.data ? adoptRow : null}
        targets={(targets.data ?? []).filter((x) => !x.node_path || x.node_path.includes(node))}
        close={() => setAdoptRow(null)}
        done={() => void q.refetch()}
      />
      <div className="table-wrap">
        <table className="grid">
          <thead>
            <tr>
              <th>{t('inv.det_col_kind')}</th>
              <th>{t('inv.det_col_what')}</th>
              <th>{t('inv.det_col_ident')}</th>
              <th>{t('inv.det_col_seen_by')}</th>
              <th>{t('inv.det_col_sources')}</th>
              <th>{t('inv.det_col_seen_at')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => {
              const { what, ident } = detectedLabel(d);
              const gwPage = `/devices/${encodeURIComponent(d.seen_by.home_id)}/${encodeURIComponent(d.seen_by.device_ref)}`;
              return (
                <tr key={d.detected_id} data-det-row={ident} data-kind={d.kind}>
                  <td>
                    <span className="chip">{t(`inv.det_kind_${d.kind}`, { defaultValue: t('common.state_unknown') })}</span>
                    {d.in_inventory && <span className="chip">{t('inv.det_in_inventory')}</span>}
                  </td>
                  <td>{what}</td>
                  <td className="mono">{ident}</td>
                  <td className="small">
                    {d.seen_by.home_name} › {d.seen_by.name || t('inv.det_gateway')}
                  </td>
                  <td className="small muted">{d.sources.map((s) => t(`inv.det_src_${s}`, { defaultValue: s })).join('、')}</td>
                  <td className="small muted">{timeStamp(d.seen_at)}</td>
                  <td>
                    {d.adopt === 'one_click' || d.adopt === 'device_button' ? (
                      <button className="btn btn-sm btn-primary" disabled={!d.in_inventory && !d.device_ref} onClick={() => (d.in_inventory ? setAdoptRow(d) : setPairAid(d.device_ref ?? ''))}>
                        {t('inv.det_add')}
                      </button>
                    ) : d.adopt === 'onvif' ? (
                      <Link className="btn btn-sm" to={gwPage}>
                        {t('inv.det_add_onvif')}
                      </Link>
                    ) : d.adopt === 'approve_address' ? (
                      <>
                        <Link className="btn btn-sm" to={gwPage}>
                          {t('inv.det_approve')}
                        </Link>
                        {d.suspect && d.suspect !== 'none' && <div className="error small">{t('inv.det_suspect')}</div>}
                      </>
                    ) : (
                      <span className="muted small">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
            {node && q.isSuccess && rows.length === 0 && (
              <tr>
                <td colSpan={7} className="muted">
                  {t('inv.det_empty')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** 庫存內的 Kinio 設備一鍵加入（收編，SPEC #vms-web-detected）：選目標家戶 → step-up → 已受限上線的馬上加入，否則設備上線就加入。 */
function AdoptDialog({ org, node, row, targets, close, done }: { org: MyOrg; node: string; row: DetectedDevice | null; targets: PairTarget[]; close(): void; done(): void }) {
  const { t } = useTranslation();
  const [home, setHome] = useState('');
  const [key, setKey] = useState(() => idempotencyKey());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [result, setResult] = useState<'joined' | 'assigned' | null>(null);
  const reset = () => {
    setHome('');
    setErr(null);
    setResult(null);
    setKey(idempotencyKey());
    close();
  };
  async function run() {
    if (!row || !home) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await detectedApi.adopt(org, node, row.detected_id, home, key);
      setResult(r.status);
      done();
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open={row != null} title={t('inv.det_adopt_title')} onClose={reset}>
      <div className="form" data-testid="det-adopt-dialog">
        {result ? (
          <p data-testid="det-adopt-result">{t(`inv.det_adopt_${result}`)}</p>
        ) : (
          <>
            <p className="muted small">{t('inv.det_adopt_help')}</p>
            <label className="field">
              <span>{t('inv.assign_home')}</span>
              <select className="input" aria-label={t('inv.assign_home')} value={home} onChange={(e) => setHome(e.target.value)}>
                <option value="">—</option>
                {targets.map((x) => (
                  <option key={x.home_id} value={x.home_id}>
                    {x.label}
                    {x.unassigned ? ` (${t('ot.unassigned')})` : ''}
                  </option>
                ))}
              </select>
            </label>
          </>
        )}
        {err != null && <p className="error">{errText(t, err)}</p>}
        <div className="dialog-actions">
          {result ? (
            <button className="btn btn-primary" onClick={reset}>
              {t('common.close')}
            </button>
          ) : (
            <>
              <button className="btn" onClick={reset}>
                {t('common.cancel')}
              </button>
              <button className="btn btn-primary" disabled={busy || !home} onClick={() => void run()} data-testid="det-adopt-run">
                {t('inv.det_add')}
              </button>
            </>
          )}
        </div>
      </div>
    </Dialog>
  );
}
